import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import { useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import { addDays, startOfToday, startOfTomorrow } from 'date-fns';
import { isAfter, isBefore } from 'date-fns/fp';
import MobileDrawer from 'feature/dispatching/components/MobileDrawer';
import AddItemButton from 'feature/dispatching/components/AddItemButton';
import { useUserRolePermissions } from 'hooks';
import DriverHeader from './DriverHeader';
import DriverCard from './DriverCard';
import DriverCardSkeleton from './DriverCardSkeleton';
import LocationFilters from './LocationFilters';
import { useViewLocationDetailsByDriverQuery } from '../driverStatusEndpoints';
import AddLocationsToDriverWizard from '../add-locations-to-driver-wizard/AddLocationsToDriverWizard';

const showV2Features = process.env.REACT_APP_DISPATCHING_VERSION >= '2.0.0';

const containerStyles = {
  /* eslint-disable-next-line */
  bgcolor: 'surface.light',
  mt: { md: 6, xs: 0 },
  pb: { md: 0, xs: 8 },
  pl: { md: 37.5, xs: 1 },
  pr: { md: 0, xs: 1 },
};

const boxStyles = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: `repeat(auto-fit, 300px)`,
  ml: 3,
};

const ViewLocationDetailsByDriver = ({ onCreate, onRemove, onView }) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewById } = useParams();
  const driverId = viewById;

  const defaultState = {
    capability: 'All',
    expiration: 'Any',
    location: '',
    status: 'All',
  };

  const [cardsFilters, setCardsFilters] = useState(defaultState);

  const {
    data = [],
    isLoading,
    isSuccess,
  } = useViewLocationDetailsByDriverQuery({ driverId }, { skip: R.isNil(viewById) });

  const locationFilter = R.compose(
    R.includes(cardsFilters.location.toUpperCase()),
    ({ locationId, name, address }) =>
      `${locationId} ${name?.toUpperCase()} ${address?.toUpperCase()}`,
    R.prop('location')
  );

  const statusFilter = R.compose(
    isActive =>
      R.or(
        cardsFilters.status === 'All',
        R.or(
          R.and(cardsFilters.status === 'Active', isActive),
          R.and(cardsFilters.status === 'Inactive', !isActive)
        )
      ),
    R.path(['location', 'isActive'])
  );

  // expiration filters
  const validFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, 'Valid')),
    isAfter(startOfToday())
  );
  const expiredFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, 'Expired')),
    isBefore(startOfTomorrow())
  );
  const almostExpiredFilter = R.both(
    R.always(R.equals(cardsFilters.expiration, '30 days')),
    R.both(isAfter(startOfToday()), isBefore(addDays(startOfToday(), 30)))
  );
  const expirationFilter = R.ifElse(
    R.always(R.equals(cardsFilters.expiration, 'Any')),
    R.T,
    R.compose(
      R.ifElse(
        R.isNil,
        R.F,
        // prettier-ignore
        R.compose(
          R.anyPass([validFilter, expiredFilter, almostExpiredFilter]), 
          d => new Date(d)
        )
      ),
      R.path(['driver', 'expirationDate'])
    )
  );

  // capability filters
  const loadFilter = R.compose(
    R.and(R.equals('Load', cardsFilters.capability)),
    R.includes(R.__, ['LOAD & UNLOAD', 'LOAD ONLY']),
    R.toUpper,
    R.path(['driver', 'loadCapabilities'])
  );
  const unloadFilter = R.compose(
    R.and(R.equals('Unload', cardsFilters.capability)),
    R.includes(R.__, ['LOAD & UNLOAD', 'UNLOAD ONLY']),
    R.toUpper,
    R.path(['driver', 'loadCapabilities'])
  );
  const capabilityFilter = R.anyPass([
    R.always(R.equals(cardsFilters.capability, 'All')),
    loadFilter,
    unloadFilter,
  ]);

  const filteredData = data
    .filter(locationFilter)
    .filter(statusFilter)
    .filter(expirationFilter)
    .filter(capabilityFilter);

  const handleResetFilters = () => {
    setCardsFilters(defaultState);
  };

  const handleRemoveFilter = byLabel => {
    setCardsFilters(current => R.assoc(byLabel, defaultState[byLabel], current));
  };

  const filtersPanel = (
    <LocationFilters
      cardsFilters={cardsFilters}
      onResetFilters={handleResetFilters}
      setCardsFilters={setCardsFilters}
    />
  );

  const [addLocationOpened, openAddLocation] = useState(false);
  const handleAddLocationOpen = R.compose(openAddLocation, R.T);
  const handleAddLocationClose = R.compose(openAddLocation, R.F);

  const renderContent = () => (
    <Box sx={boxStyles}>
      {isLoading && (
        <>
          <DriverCardSkeleton />
          <DriverCardSkeleton />
          <DriverCardSkeleton />
        </>
      )}

      {showV2Features && isDispatchingManage && isSuccess && (
        <AddItemButton itemName="Location" onAdd={handleAddLocationOpen} />
      )}

      {isSuccess &&
        filteredData.map(({ driver, location }) => (
          <DriverCard
            driver={driver}
            key={location.locationId}
            location={location}
            onRemove={onRemove}
            onView={onView}
            subheader={location.address}
            title={`${location.locationId} ${R.toUpper(location.name)}`}
          />
        ))}
      {addLocationOpened && (
        <AddLocationsToDriverWizard
          onClose={handleAddLocationClose}
          openDrawer={addLocationOpened}
        />
      )}
    </Box>
  );

  return lgUp ? (
    <Box sx={containerStyles}>
      <DriverHeader
        cardsFilters={cardsFilters}
        defaultFiltersState={defaultState}
        filtersPanel={filtersPanel}
        onCreate={onCreate}
        onRemoveFilter={handleRemoveFilter}
      />
      {renderContent()}
    </Box>
  ) : (
    <MobileDrawer
      backLabel="Back to Drivers"
      createLabel="New Driver"
      filtersPanel={filtersPanel}
      module="compliance/driver-status"
      open={!!viewById}
      setTitle={() => {}}
    >
      {renderContent()}
    </MobileDrawer>
  );
};

ViewLocationDetailsByDriver.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

export default ViewLocationDetailsByDriver;
